import { useEffect } from "react";

// Only start a settled, mostly visible slide. Recover stalled requests without
// resetting healthy playback, changing the native loop, or playing hidden media.
export const useVisibleVideo = (videoRef, sourceKey) => {
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    let visible = false;
    let settled = false;
    let settleTimer;
    let lastTime = video.currentTime;
    let lastProgress = performance.now();
    let retried = false;
    let reloads = 0;
    let resumeTime = null;
    const shouldPlay = () => visible && settled && !preference.matches && !document.hidden;
    const play = () => video.play().catch(() => {}); // A navigation pause can reject a pending play.
    const syncPlayback = () => {
      if (shouldPlay()) {
        if (video.paused) play();
      } else {
        video.pause();
        lastProgress = performance.now();
      }
    };
    const restorePosition = () => {
      if (resumeTime !== null && Number.isFinite(video.duration)) {
        video.currentTime = Math.min(resumeTime, Math.max(0, video.duration - 0.1));
        resumeTime = null;
      }
      syncPlayback();
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
      settled = false;
      clearTimeout(settleTimer);
      lastProgress = performance.now();
      lastTime = video.currentTime;
      retried = false;
      reloads = 0;
      syncPlayback();
      if (visible) settleTimer = setTimeout(() => {
        settled = true;
        syncPlayback();
      }, 180);
    }, { threshold: [0, 0.5] });
    observer.observe(video.closest("section") || video);

    const watchdog = setInterval(() => {
      if (!shouldPlay()) return;
      const now = performance.now();
      if (Math.abs(video.currentTime - lastTime) > 0.02) {
        lastTime = video.currentTime;
        lastProgress = now;
        retried = false;
        return;
      }
      const stalledFor = now - lastProgress;
      if (stalledFor > 7000 && reloads < 2) {
        // Bounded recovery for a truly stagnant fetch; retain the current position.
        resumeTime = video.currentTime;
        reloads += 1;
        lastProgress = now;
        retried = false;
        video.load();
        play();
      } else if (stalledFor > 3000 && !retried) {
        retried = true;
        video.pause();
        play();
      }
    }, 1000);
    video.addEventListener("loadedmetadata", restorePosition);
    video.addEventListener("canplay", syncPlayback);
    preference.addEventListener("change", syncPlayback);
    document.addEventListener("visibilitychange", syncPlayback);
    return () => {
      observer.disconnect();
      clearTimeout(settleTimer);
      clearInterval(watchdog);
      video.removeEventListener("loadedmetadata", restorePosition);
      video.removeEventListener("canplay", syncPlayback);
      preference.removeEventListener("change", syncPlayback);
      document.removeEventListener("visibilitychange", syncPlayback);
      video.pause();
    };
  }, [videoRef, sourceKey]);
};